import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { useToast } from 'use-toast-mui';
import { Box, FormControl, FormControlLabel, RadioGroup, Collapse } from '@mui/material';
import { getAllCentersList } from '../api/store-management';
import { pageSize } from '../constants/constants';
import { FormInputText } from '../form-components/FormInputText';
import AnimationPage from '../pages/AnimationPage';
import CustomRadioBox from './CustomRadioBox';
import { FormInputDropdown } from './form/FormInputDropdown';
import Loader from './loader';
import StoreTable from './storeTabs/StoreTable';
import { errorMessages } from '../utils/methods';
import { useTranslation } from 'react-i18next';
import CommonFilterActionButton from '../form-components/CommonFilterActionButton';
import CommonSearchSubmitButton from '../form-components/CommonSearchSubmitButton';
import CommonPagination from '../form-components/CommonPagination';
import CommonFilterToggle from '../form-components/CommonFilterToggle';
import { styled } from '@mui/material/styles';

const initialQuery = {
  page: 1,
  limit: 10,
  branchName: '',
  type: '',
};

var qs = require('qs');

// ============================================
// Styled Components
// ============================================
const PageContainer = styled(Box)({
  background: '#fff',
  borderRadius: '10px',
  padding: '30px 40px 60px 40px',
});

const HeaderRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const SearchSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const FilterRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginTop: '24px',
});

const FilterLabel = styled('span')({
  width: '97px',
  minWidth: '97px',
  fontSize: '16px',
  fontWeight: 500,
});

const SearchButtonRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
});

const LimitSelectRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: '24px',
});

const PageTitle = styled('span')({
  fontSize: '18px',
  fontWeight: 700,
  color: '#111',
  marginRight: '16px',
  whiteSpace: 'nowrap',
});

// eslint-disable-next-line no-unused-vars -- 신규지점 등록 버튼 숨김 중
const CreateButton = styled('a')({
  fontSize: '13px',
  fontWeight: 600,
  borderRadius: '8px',
  height: '40px',
  padding: '0 20px',
  backgroundColor: '#15AFB7',
  color: '#fff',
  border: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  textDecoration: 'none',
  '&:hover': {
    backgroundColor: '#0F9DA5',
    color: '#fff',
  },
});

const LoaderContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  padding: '40px 0',
});

const TableContainer = styled(Box)({
  marginTop: '8px',
});

const StoreManagement = () => {
  const toast = useToast();
  const { t } = useTranslation();
  const token = useSelector(function (state) {
    return state.user.user.token;
  });
  const user = useSelector(function (state) {
    return state.user.user;
  });
  // 본점(HEAD) 계정만 전체 지점 목록을 본다. 지점(BRANCH) 계정은 목록 없이 자기 센터 세팅으로 바로 이동.
  const isBranchAccount = user?.centerType === 'BRANCH';
  // eslint-disable-next-line no-unused-vars -- 신규지점 등록 버튼 숨김 중
  const centerType = useSelector(function (state) {
    return (
      state.user.user?.centerType === 'HEAD' &&
      state.user.user?.headBranch?.headBranchId &&
      state.user.user?.userRole === 'ROLE_MANAGER'
    );
  });

  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState(() => {
    const saved = sessionStorage.getItem('storeSearchConditions');
    if (saved) {
      sessionStorage.removeItem('storeSearchConditions');
      try { return JSON.parse(saved); } catch { return initialQuery; }
    }
    return initialQuery;
  });
  const [centersList, setCentersList] = useState();
  const [pages, setPages] = useState();
  const [filterOpen, setFilterOpen] = useState(false);

  const { handleSubmit, watch, setValue, control, reset } = useForm({ defaultValues: initialQuery });

  const watchedType = watch('type');

  const handleCheckedBulk = () => {
    setValue('type', '');
    setQuery({ ...query, type: initialQuery.type });
  };

  function handleChangePage(event, value) {
    setQuery({ ...query, page: value });
  }

  const handleCheckedMember = type => {
    setValue('type', type);
    setQuery({ ...query, type: type });
  };

  useEffect(() => {
    if (isBranchAccount) return; // 지점 계정은 목록 조회 없이 바로 리다이렉트
    getCentersList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, isBranchAccount]);

  const getCentersList = async () => {
    const queryString = qs.stringify(query);
    try {
      setLoading(true);
      await getAllCentersList(token, queryString).then(result => {
        setLoading(false);
        setCentersList(result.content);
        setPages(result.totalElements);
      });
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  const submit = async values => {
    setQuery({
      ...query,
      ...values,
    });
  };

  const setInitial = () => {
    setQuery(initialQuery);
    reset(initialQuery);
  };

  if (isBranchAccount && user?.branch?.centerId) {
    return <Navigate to={`/store/${user.branch.centerId}`} replace />;
  }

  return (
    <AnimationPage>
      <PageContainer>
        {/* 헤더: 검색 + 신규지점 등록 */}
        <HeaderRow>
          <SearchSection>
            <PageTitle>센터 검색</PageTitle>
            <FormInputText
              control={control}
              name="branchName"
              sx={{ width: '370px' }}
              placeholder="센터명을 검색해 주세요"
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  handleSubmit(submit)();
                }
              }}
            />
            <CommonSearchSubmitButton onClick={handleSubmit(submit)}>
              {t('Search')}
            </CommonSearchSubmitButton>
          </SearchSection>
          {/* 신규지점 등록 버튼 숨김 (2026-10-16). 복구 시 Link import 도 되살릴 것
          {centerType && (
            <CreateButton as={Link} to="/store/create">
              신규지점 등록
            </CreateButton>
          )} */}
        </HeaderRow>

        {/* 필터 */}
        <Collapse in={filterOpen}>
          <FilterRow>
            <FilterLabel>{t('Branch type')}</FilterLabel>
            <FormControl>
              <RadioGroup row>
                <FormControlLabel
                  sx={{ mr: 2.5 }}
                  control={<CustomRadioBox checked={watchedType === ''} onChange={handleCheckedBulk} />}
                  label={t('View All')}
                />
                <FormControlLabel
                  sx={{ mr: 2.5 }}
                  control={
                    <CustomRadioBox checked={watchedType === 'HEAD'} onChange={() => handleCheckedMember('HEAD')} />
                  }
                  label={t('HEAD')}
                />
                <FormControlLabel
                  sx={{ mr: 2.5 }}
                  control={
                    <CustomRadioBox checked={watchedType === 'BRANCH'} onChange={() => handleCheckedMember('BRANCH')} />
                  }
                  label={t('BRANCH')}
                />
              </RadioGroup>
            </FormControl>
          </FilterRow>

          <SearchButtonRow sx={{ mt: 2 }}>
            <CommonFilterActionButton variant="primary" onClick={handleSubmit(submit)}>
              {t('Search for store with set conditions')}
            </CommonFilterActionButton>
            <CommonFilterActionButton variant="secondary" onClick={setInitial}>
              {t('Reset search conditions')}
            </CommonFilterActionButton>
          </SearchButtonRow>
        </Collapse>

        <CommonFilterToggle open={filterOpen} onClick={() => setFilterOpen(prev => !prev)} />

        {/* 건수 + 페이지 사이즈 */}
        <LimitSelectRow>
          <span style={{ fontSize: '14px', fontWeight: 500, color: '#333' }}>총 {pages || 0}건</span>
          <FormInputDropdown
            name="limit"
            control={control}
            options={pageSize}
            staticValue={query.limit}
            onChange={e => setQuery({ ...query, limit: e.target.value, page: 1 })}
            sx={{ width: '108px' }}
          />
        </LimitSelectRow>

        {/* 테이블 */}
        <TableContainer>
          {loading ? (
            <LoaderContainer>
              <Loader />
            </LoaderContainer>
          ) : (
            <>
              <StoreTable centersList={centersList} getCentersList={getCentersList} setCentersList={setCentersList} onBeforeNavigate={() => sessionStorage.setItem('storeSearchConditions', JSON.stringify(query))} />
              <CommonPagination
                totalElements={pages}
                limit={query.limit}
                page={query.page}
                onChange={handleChangePage}
              />
            </>
          )}
        </TableContainer>
      </PageContainer>
    </AnimationPage>
  );
};

export default StoreManagement;
